import { useQueryClient } from "@tanstack/react-query";
import format from "date-fns/format";
import { boronClient } from "../../../api";
import {
  ForbiddenError,
  TooManyRequestsError,
  UnprocessableEntityError,
} from "../../../errors";
import { useBoronMutation } from "../../../hooks/http/useBoronMutation";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { buildAiAssistantKarteGenerationStatusQueryKey } from "./useFetchAiAssistantKarteGenerationStatus";
import type { AiAssistantKarteGenerationFormValues } from "./validationSchema";

const buildRequestBody = (values: AiAssistantKarteGenerationFormValues) => ({
  ai_assistant_template_id: values.aiAssistantTemplateId ?? undefined,
  start_date: format(values.startDate, "yyyy-MM-dd"),
  end_date: format(values.endDate, "yyyy-MM-dd"),
});

export const useRequestAiAssistantKarteGeneration = ({
  studentId,
}: {
  studentId: string;
}) => {
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();
  const queryClient = useQueryClient();

  // GEN-04: 成功時・429失敗時は進行状態を即時再取得する
  // （古い acceptable: true のポーリング結果によるボタン再活性・再押下ループを防ぐ）
  const invalidateGenerationStatus = () => {
    queryClient.invalidateQueries({
      queryKey: buildAiAssistantKarteGenerationStatusQueryKey(studentId),
    });
  };

  return useBoronMutation(
    async (values: AiAssistantKarteGenerationFormValues) =>
      await boronClient.POST(
        "/api/v1/students/{student_id}/ai_assistant_kartes",
        {
          params: { path: { student_id: studentId } },
          body: buildRequestBody(values),
        },
      ),
    {
      onSuccess: () => {
        showSuccessMessage("カルテを生成中です");
        invalidateGenerationStatus();
      },
      onError: (error) => {
        if (error instanceof ForbiddenError) {
          showErrorMessage("この生徒のAIカルテ生成は利用できません");
          return;
        }
        if (error instanceof UnprocessableEntityError) {
          const messages = error
            .getErrorMessages()
            .filter((message): message is string => Boolean(message));
          showErrorMessage(
            messages.length > 0
              ? messages.join("\n")
              : "入力内容を確認してください",
          );
          return;
        }
        if (error instanceof TooManyRequestsError) {
          showErrorMessage("カルテ生成のリクエストが既に進行中です");
          invalidateGenerationStatus();
          return;
        }
        showErrorMessage("カルテを生成できませんでした");
      },
    },
  );
};
